<script setup lang="ts">
// Assets
import imgUrl from '../../../assets/clinishift-logo-manager-portal-short.png'

// Vue & Plugins
import { computed } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import { storeToRefs } from 'pinia'

// Components
import UserProfileMenu from '../../../user/components/UserProfileMenu.vue'

// Composables
import { useHasPermission } from '../../../permissions/composables/useHasPermission'
import { useNavigationDrawers } from '../../composables/useNavDrawer'
import { hasNavItem } from '../../../router'

// Models
import { User } from '../../../users/models/user'

// Stores
import { useAuthSiteStore } from '../../../user/authSiteStore'
import { useAuthUserStore } from '../../../user/authUserStore'

// Other
import { useNotificationStore } from '../../../notifications/notificationStore'

defineProps<{
  user: User,
}>()

const router = useRouter()
const route = useRoute()

const authSiteStore = useAuthSiteStore()
const { sites, selected: selectedSite } = storeToRefs(authSiteStore)

const authUserStore = useAuthUserStore()

const notificationStore = useNotificationStore()
const { pagination } = storeToRefs(notificationStore)
const total = computed(() => pagination.value.total)

const { can } = useHasPermission()

const headerNavRoutes = computed(() => {
  return router.getRoutes()
    .filter((route) => {
      if (!route.meta.navConfig?.navHeader) {
        return false
      }

      if (route.meta.permission) {
        return can(route.meta.permission.ability,route.meta.permission.subject)
      }

      return true
    })
    .sort((a, b) => {
      return (a.meta.navConfig?.order ?? 0) - (b.meta.navConfig?.order ?? 0)
    })
})

const headerNavItems = computed(() => {
  return headerNavRoutes.value
    .map((route) => route.meta)
    .filter(hasNavItem)
    .map((meta) => {
      return meta.navItem
    })
})

async function logOutPressed () {
  try { 
    authUserStore.signOut()
  } catch (error) {
    console.log('logout error', error)
  }
}

const {
  openDrawer,
  closeDrawer,
  isDrawerOpen,
} = useNavigationDrawers()

const isLeftDrawerOpen = isDrawerOpen('left')

const leftDrawerRoutes = computed(() => {
  return router.getRoutes().filter((route => route.meta.navItem && route.meta.navConfig?.leftDrawer))
})

onBeforeRouteUpdate(() => {
  closeDrawer('left')
})

const viewNotificationsPressed = () => router.push({ name: '/(app)/dashboard/' })

const newShiftPressed = () => router.push({ name: '/(app)/schedule/shifts.wizard' })

const { canManageShifts } = useHasPermission()
</script>

<template>
  <!-- TODO: THEME - Forced bg breaks dark mode -->
  <nav
    class="bg-primary-900"
  >
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between">
        <!-- Left Side Nav -->
        <div class="flex items-center gap-2">
          <!-- TODO: THEME - Forced colour breaks dark mode -->
          <ToggleButton
            class="bg-transparent text-white border-none"
            :model-value="isLeftDrawerOpen"
            @update:model-value="(value) => value ? openDrawer('left') : closeDrawer('left')"
          >
            <template #default>
              <i
                :class="{
                  'pi pi-times': isLeftDrawerOpen,
                  'pi pi-bars': !isLeftDrawerOpen
                }"
              />
            </template>
          </ToggleButton>

          <div class="flex-shrink-0">
            <!-- TODO: THEME - Forced image breaks dark mode -->
            <Image
              width="32"
              height="32"
              :src="imgUrl"
              alt="CliniShift Logo"
            />
          </div>
          <div class="hidden md:block">
            <div class="ml-10 flex space-x-4">
              <!-- TODO: THEME - Forced colour breaks dark mode -->
              <Button
                v-for="item in headerNavItems"
                :key="item.label"
                :to="{ name: item.route }"
                class="ml-2 font-bold"
                :class="{
                  'bg-highlight-emphasis hover:bg-highlight': route.matched.some(({ name }) => name === item.route),
                  'text-slate-50 hover:text-color': route.matched.some(({ name }) => name !== item.route),
                }"
                as="router-link"
                :label="item.label"
                :severity="route.matched.some(({ name }) => name === item.route) ? 'primary' : 'secondary'"
                text
              >
                <span>{{ item.label }}</span>
              </Button>
            </div>
          </div>
        </div>
        <!-- Right Side Nav -->
        <div class="flex items-center gap-x-4">
          <Button
            v-if="canManageShifts"
            label="New Shift"
            aria-label="New Shift"
            @click="newShiftPressed"
          />

          <!-- TODO: THEME - Forced colour breaks dark mode -->
          <OverlayBadge
            :value="total"
            size="small"
            :pt:pc-badge:root:class="{ '!hidden': total === 0 }"
          >
            <Button 
              class="text-white hover:text-color"
              icon="pi pi-bell"
              text
              aria-label="View notifications"
              @click="viewNotificationsPressed"
            />
          </OverlayBadge>

          <Select
            v-model="selectedSite"
            :options="sites"
            option-label="name"
          />

          <!-- Profile dropdown -->
          <UserProfileMenu
            :user="user"
            @log-out="logOutPressed"
          />
        </div>
      </div>
    </div>
  </nav>

  <Drawer
    :pt:content:class="'pr-0'"
    :visible="isLeftDrawerOpen"
    @update:visible="(value) => value ? openDrawer('left') : closeDrawer('left')"
  >
    <nav>
      <ul>
        <li
          v-for="item in leftDrawerRoutes"
          :key="item.name"
        >
          <Button
            :to="{ name: item.name }"
            class="justify-start"
            :class="{
              'bg-highlight-emphasis hover:bg-highlight': route.matched.some(({ name }) => name === item.name),
            }"
            as="router-link"
            :label="item.meta.navItem?.label"
            :icon="item.meta.navItem?.icon"
            :severity="route.matched.some(({ name }) => name === item.meta.navItem?.route) ? 'primary' : 'secondary'"
            size="large"
            text
            fluid
          />
        </li>
      </ul>
    </nav>
  </Drawer>
</template>
